/**
 * Timeline Builder — Constructs unified chronological timeline
 *
 * Merges navigation steps and API call steps into a single sorted array.
 * Each entry carries a type discriminator, sequential index, and test identity.
 */

import type {
  ActionStep,
  ApiCallRecord,
  ApiAssertion,
  NavigationAnnotation,
  NavigationType,
  FailureDiagnostic,
  NetworkStats,
  TestStatus,
  TestType,
  TestResult as TRTestResult,
  TestArtifacts,
  ConsoleLogEntry,
  CapturedNetworkRequest,
  TimelineStep,
  NavigationStep,
  ApiCallStep,
  StepTestIdentity,
  StepAssertion,
  ApiCallStepResponse,
} from '@testrelic/core';

// ---------------------------------------------------------------------------
// CollectedTest fields needed for timeline building
// ---------------------------------------------------------------------------

/** Test data passed to the timeline builder. */
export interface TimelineTestData {
  readonly titlePath: string[];
  readonly title: string;
  readonly status: TestStatus;
  readonly duration: number;
  readonly startedAt: string;
  readonly completedAt: string;
  readonly retryCount: number;
  readonly retry: number;
  readonly tags: string[];
  readonly failure: FailureDiagnostic | null;
  readonly specFile: string;
  readonly navigations: NavigationAnnotation[];
  readonly apiCalls: ApiCallRecord[] | null;
  readonly apiAssertions: ApiAssertion[] | null;
  // Full test result fields for backward-compatible `tests[]` array
  readonly testId: string;
  readonly filePath: string;
  readonly suiteName: string;
  readonly testType: TestType;
  readonly isFlaky: boolean;
  readonly retryStatus: string | null;
  readonly expectedStatus: TestStatus;
  readonly actualStatus: TestStatus;
  readonly artifacts: TestArtifacts | null;
  readonly networkRequests: CapturedNetworkRequest[] | null;
  readonly actions: ActionStep[] | null;
  readonly consoleLogs: ConsoleLogEntry[] | null;
}

// ---------------------------------------------------------------------------
// Configuration for timeline building
// ---------------------------------------------------------------------------

export interface TimelineBuildOptions {
  readonly navigationTypes: NavigationType[] | null;
}

// ---------------------------------------------------------------------------
// Internal unindexed step type (before index assignment)
// ---------------------------------------------------------------------------

interface UnindexedStep {
  readonly type: 'navigation' | 'api_call';
  readonly url: string;
  readonly timestamp: string;
  readonly specFile: string;
  readonly test: StepTestIdentity;
  readonly tests: TRTestResult[];
  // Navigation-specific
  durationOnUrl?: number;
  readonly navigationType?: NavigationType;
  readonly domContentLoadedAt?: string | null;
  readonly networkIdleAt?: string | null;
  readonly networkStats?: NetworkStats | null;
  // API call-specific
  readonly callId?: string;
  readonly method?: string;
  readonly responseTime?: number | null;
  readonly request?: { readonly headers: Record<string, string> | null; readonly body: unknown };
  readonly response?: ApiCallStepResponse | null;
  readonly error?: string | null;
  readonly assertions?: readonly StepAssertion[];
  // Track source test for duration recalculation
  readonly _testTitle: string;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build a unified timeline from collected test data.
 * Merges navigation and API call steps, sorts chronologically,
 * recalculates navigation durations, and assigns sequential indices.
 */
export function buildUnifiedTimeline(
  tests: readonly TimelineTestData[],
  options: TimelineBuildOptions,
): TimelineStep[] {
  const allSteps: UnindexedStep[] = [];

  for (const test of tests) {
    const identity = buildTestIdentity(test);
    const testResult = toTestResult(test);
    const navSteps = buildNavigationSteps(test, identity, testResult, options);
    const apiSteps = buildApiCallSteps(test, identity, testResult);

    // If test has no navigations and no API calls, produce a dummy nav entry
    if (navSteps.length === 0 && apiSteps.length === 0) {
      allSteps.push({
        type: 'navigation',
        url: 'about:blank',
        timestamp: test.startedAt,
        durationOnUrl: test.duration,
        navigationType: 'dummy',
        domContentLoadedAt: null,
        networkIdleAt: null,
        networkStats: null,
        specFile: test.specFile,
        test: identity,
        tests: [testResult],
        _testTitle: test.title,
      });
      continue;
    }

    allSteps.push(...navSteps, ...apiSteps);
  }

  // Sort by timestamp, then navigation before api_call, then by callId
  allSteps.sort(compareSteps);

  // Recalculate navigation durations based on unified timeline
  recalculateNavigationDurations(allSteps, tests);

  // Assign sequential indices and produce final typed steps
  return allSteps.map((step, i) => {
    if (step.type === 'navigation') {
      const navStep: NavigationStep = {
        index: i,
        type: 'navigation',
        url: step.url,
        timestamp: step.timestamp,
        durationOnUrl: step.durationOnUrl ?? 0,
        navigationType: step.navigationType!,
        domContentLoadedAt: step.domContentLoadedAt ?? null,
        networkIdleAt: step.networkIdleAt ?? null,
        networkStats: step.networkStats ?? null,
        specFile: step.specFile,
        test: step.test,
      };
      // Add backward-compatible fields via spread
      return { ...navStep, tests: step.tests } as NavigationStep & { tests: TRTestResult[] };
    }

    const apiStep: ApiCallStep = {
      index: i,
      type: 'api_call',
      callId: step.callId!,
      method: step.method!,
      url: step.url,
      timestamp: step.timestamp,
      responseTime: step.responseTime ?? null,
      request: step.request!,
      response: step.response ?? null,
      ...(step.error ? { error: step.error } : {}),
      assertions: step.assertions ?? [],
      specFile: step.specFile,
      test: step.test,
    };
    return { ...apiStep, tests: step.tests } as ApiCallStep & { tests: TRTestResult[] };
  }) as TimelineStep[];
}

// ---------------------------------------------------------------------------
// Step builders
// ---------------------------------------------------------------------------

function buildTestIdentity(test: TimelineTestData): StepTestIdentity {
  return {
    title: test.title,
    fullTitle: test.titlePath,
    status: test.status,
    duration: test.duration,
    retries: test.retryCount,
    retry: test.retry,
    tags: test.tags,
    failure: test.failure,
  };
}

function toTestResult(test: TimelineTestData): TRTestResult {
  return {
    title: test.title,
    status: test.status,
    duration: test.duration,
    startedAt: test.startedAt,
    completedAt: test.completedAt,
    retryCount: test.retryCount,
    tags: test.tags,
    failure: test.failure,
    testId: test.testId,
    filePath: test.filePath,
    suiteName: test.suiteName,
    testType: test.testType,
    isFlaky: test.isFlaky,
    retryStatus: test.retryStatus,
    expectedStatus: test.expectedStatus,
    actualStatus: test.actualStatus,
    artifacts: test.artifacts,
    networkRequests: test.networkRequests,
    apiCalls: test.apiCalls,
    apiAssertions: test.apiAssertions,
    actions: test.actions,
    consoleLogs: test.consoleLogs,
  };
}

function buildNavigationSteps(
  test: TimelineTestData,
  identity: StepTestIdentity,
  testResult: TRTestResult,
  options: TimelineBuildOptions,
): UnindexedStep[] {
  const steps: UnindexedStep[] = [];

  for (const nav of test.navigations) {
    // Apply navigation type filter
    if (options.navigationTypes !== null && !options.navigationTypes.includes(nav.navigationType)) {
      continue;
    }

    steps.push({
      type: 'navigation',
      url: nav.url,
      timestamp: nav.timestamp,
      durationOnUrl: 0, // placeholder — recalculated after sorting
      navigationType: nav.navigationType,
      domContentLoadedAt: nav.domContentLoadedAt ?? null,
      networkIdleAt: nav.networkIdleAt ?? null,
      networkStats: nav.networkStats ?? null,
      specFile: test.specFile,
      test: identity,
      tests: [testResult],
      _testTitle: test.title,
    });
  }

  return steps;
}

function buildApiCallSteps(
  test: TimelineTestData,
  identity: StepTestIdentity,
  testResult: TRTestResult,
): UnindexedStep[] {
  if (!test.apiCalls || test.apiCalls.length === 0) {
    return [];
  }

  const assertions = test.apiAssertions ?? [];

  return test.apiCalls.map((call): UnindexedStep => {
    // Filter assertions for this call
    const linkedAssertions: StepAssertion[] = assertions
      .filter((a) => a.callId === call.id)
      .map((a) => ({
        type: a.type,
        expected: a.expected,
        actual: a.actual,
        status: a.status,
        location: a.location,
        ...(a.expression !== undefined ? { expression: a.expression } : {}),
      }));

    // Build response, null on error
    let response: ApiCallStepResponse | null = null;
    if (call.responseStatusCode !== null && call.responseStatusText !== null) {
      response = {
        statusCode: call.responseStatusCode,
        statusText: call.responseStatusText,
        headers: call.responseHeaders,
        body: parseBody(call.responseBody),
      };
    }

    return {
      type: 'api_call',
      callId: call.id,
      method: call.method,
      url: call.url,
      timestamp: call.timestamp,
      responseTime: call.error ? null : call.responseTimeMs,
      request: {
        headers: call.requestHeaders,
        body: parseBody(call.requestBody),
      },
      response,
      ...(call.error ? { error: call.error } : {}),
      assertions: linkedAssertions,
      specFile: test.specFile,
      test: identity,
      tests: [testResult],
      _testTitle: test.title,
    };
  });
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

function compareSteps(a: UnindexedStep, b: UnindexedStep): number {
  const timeA = new Date(a.timestamp).getTime();
  const timeB = new Date(b.timestamp).getTime();

  if (timeA !== timeB) return timeA - timeB;

  // Same timestamp: navigation before api_call
  const typeOrder = { navigation: 0, api_call: 1 } as const;
  const typeA = typeOrder[a.type];
  const typeB = typeOrder[b.type];
  if (typeA !== typeB) return typeA - typeB;

  // Same type and timestamp: sort API calls by callId numeric suffix
  if (a.type === 'api_call' && b.type === 'api_call' && a.callId && b.callId) {
    return extractCallNumber(a.callId) - extractCallNumber(b.callId);
  }

  return 0;
}

function extractCallNumber(callId: string): number {
  const match = callId.match(/(\d+)$/);
  return match ? parseInt(match[1], 10) : 0;
}

// ---------------------------------------------------------------------------
// Duration recalculation
// ---------------------------------------------------------------------------

function recalculateNavigationDurations(
  steps: UnindexedStep[],
  tests: readonly TimelineTestData[],
): void {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (step.type !== 'navigation') continue;

    const navTime = new Date(step.timestamp).getTime();

    // Find the next step from the same test
    let nextStepTime: number | null = null;
    for (let j = i + 1; j < steps.length; j++) {
      if (steps[j]._testTitle === step._testTitle) {
        nextStepTime = new Date(steps[j].timestamp).getTime();
        break;
      }
    }

    if (nextStepTime !== null) {
      step.durationOnUrl = Math.max(0, nextStepTime - navTime);
    } else {
      // Last step for this test — use test completedAt
      const test = tests.find((t) => t.title === step._testTitle);
      if (test) {
        const endTime = new Date(test.completedAt).getTime();
        step.durationOnUrl = Math.max(0, endTime - navTime);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Body parsing helper
// ---------------------------------------------------------------------------

function parseBody(body: string | null): unknown {
  if (body === null || body === undefined) return null;
  try {
    return JSON.parse(body) as unknown;
  } catch {
    return body;
  }
}
